import defaultStorage from 'redux-persist/lib/storage';
import { ENV } from '../../config/env';
import { encryptResponse, decryptString } from '../../services/encrypt';

/**
 * App-level secret used ONLY for encrypting the redux-persist blob at
 * rest in localStorage. Override it per environment via
 * `REACT_APP_PERSIST_SECRET`. This is intentionally separate from the
 * login (organization_id + user_id) key, because the persisted store
 * must be decryptable on app boot, before any user has logged in.
 */
const PERSIST_SECRET = ENV.PERSIST_SECRET;

/**
 * A redux-persist storage engine that transparently encrypts every
 * value written to localStorage (AES-256-GCM) and decrypts it on
 * rehydrate. The redux-persist storage API is Promise-based, so the
 * async WebCrypto (`crypto.subtle`) calls fit naturally here.
 *
 * In-memory redux state stays decrypted (so all existing selectors keep
 * working), while anything that lands in localStorage is ciphertext.
 */
const encryptedStorage = {
    getItem: async (key: string): Promise<string | null> => {
        const stored = await defaultStorage.getItem(key);

        if (stored == null) {
            return null;
        }

        try {
            return await decryptString(stored, PERSIST_SECRET);
        } catch {
            // Fallback for a pre-existing plaintext persist blob (first
            // run after enabling encryption) or a corrupted value. The
            // next write will re-store it encrypted.
            return stored;
        }
    },

    setItem: async (key: string, value: string): Promise<void> => {
        const encrypted = await encryptResponse(value, PERSIST_SECRET);
        await defaultStorage.setItem(key, encrypted);
    },

    removeItem: async (key: string): Promise<void> => {
        await defaultStorage.removeItem(key);
    },
};

export default encryptedStorage;
